import { isAxiosError } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";

import { OutcomeTreeImportPreview } from "./OutcomeTreeImportPreview";
import { countExportedOutcomes } from "./outcome-export-file-utils";

import {
  OutcomeImportPreview,
  serverImportOutcomeTree,
  serverPreviewOutcomeImport,
} from "api/api-server-outcome-transfer";
import { OrgType } from "components/server-types";
import { t } from "i18n/i18n";
import { Banner } from "mds/components/Banner";
import { Dialog } from "mds/components/Dialog";
import { FileUploadField } from "mds/components/FileUploadField";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { storeApi } from "store/index";

const MAX_OUTCOME_EXPORT_FILE_BYTES = 2 * 1024 * 1024;
type OutcomeImportError = { message: string; details?: string[] };
type OutcomeImportOperation = "preview" | "import";

function getOutcomeImportError(
  error: unknown,
  operation: OutcomeImportOperation,
): OutcomeImportError {
  const responseData = isAxiosError<{ code?: string; details?: string[] }>(error)
    ? error.response?.data
    : undefined;
  switch (responseData?.code) {
    case "conflicts":
      return { message: t("outcome_transfer.conflicts"), details: responseData.details };
    case "invalid_file":
      return { message: t("outcome_transfer.invalid_file") };
    case "preview_expired":
      return { message: t("outcome_transfer.preview_expired") };
    default:
      return {
        message: t(
          operation === "import" ? "outcome_transfer.import_unknown" : "outcome_transfer.failed",
        ),
      };
  }
}

export function OutcomeImportDialog({ org, onClose }: { org: OrgType; onClose: () => void }) {
  const [outcomeImportPreview, setOutcomeImportPreview] = useState<OutcomeImportPreview>();
  const [outcomeExportFilename, setOutcomeExportFilename] = useState("");
  const [outcomeImportError, setOutcomeImportError] = useState<OutcomeImportError>();
  const [isRequestPending, setIsRequestPending] = useState(false);
  const [importedOutcomeCount, setImportedOutcomeCount] = useState<number>();
  const requestAbortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      requestAbortControllerRef.current?.abort();
    };
  }, []);

  const maybeStartOutcomeImportRequest = useCallback(() => {
    if (requestAbortControllerRef.current) return;
    const abortController = new AbortController();
    requestAbortControllerRef.current = abortController;
    setIsRequestPending(true);
    setOutcomeImportError(undefined);
    return abortController;
  }, []);

  const previewOutcomeExportFile = useCallback(
    async (outcomeExportFile: File) => {
      // Ignore a second drop before changing any state owned by the in-flight preview.
      if (requestAbortControllerRef.current) return;
      setOutcomeImportPreview(undefined);
      setOutcomeExportFilename(outcomeExportFile.name);
      if (outcomeExportFile.size > MAX_OUTCOME_EXPORT_FILE_BYTES) {
        setOutcomeImportError({ message: t("outcome_transfer.file_too_large") });
        return;
      }

      const abortController = maybeStartOutcomeImportRequest();
      if (!abortController) return;
      try {
        let outcomeExportData: unknown;
        try {
          outcomeExportData = JSON.parse(await outcomeExportFile.text());
        } catch {
          if (isMountedRef.current) {
            setOutcomeImportError({ message: t("outcome_transfer.invalid_file") });
          }
          return;
        }
        if (!isMountedRef.current) return;
        const outcomeImportPreviewResult = await serverPreviewOutcomeImport(
          org.id,
          outcomeExportData,
          abortController.signal,
        );
        if (isMountedRef.current) setOutcomeImportPreview(outcomeImportPreviewResult);
      } catch (cause) {
        if (isMountedRef.current) setOutcomeImportError(getOutcomeImportError(cause, "preview"));
      } finally {
        requestAbortControllerRef.current = null;
        setIsRequestPending(false);
      }
    },
    [org.id, maybeStartOutcomeImportRequest],
  );

  const importOutcomeTree = useCallback(async () => {
    if (!outcomeImportPreview) return;
    const abortController = maybeStartOutcomeImportRequest();
    if (!abortController) return;
    try {
      const importResult = await serverImportOutcomeTree(
        org.id,
        outcomeImportPreview.import_token,
        abortController.signal,
      );
      if (!isMountedRef.current) return;
      setImportedOutcomeCount(importResult.count);
      try {
        await Promise.all([
          storeApi.outcome_groups.list(undefined, { skipCache: true, skipToast: true }),
          storeApi.outcome_subgroups.list(undefined, { skipCache: true, skipToast: true }),
          storeApi.outcomes.list(undefined, { skipCache: true, skipToast: true }),
        ]);
      } catch {
        // The import already committed; keep the success state while reporting the stale index.
        if (isMountedRef.current) {
          setOutcomeImportError({ message: t("outcome_transfer.refresh_failed") });
        }
      }
    } catch (cause) {
      if (isMountedRef.current) {
        setOutcomeImportError(getOutcomeImportError(cause, "import"));
        // A lost response may still have committed server-side, so do not offer an immediate retry.
        setOutcomeImportPreview(undefined);
      }
    } finally {
      requestAbortControllerRef.current = null;
      setIsRequestPending(false);
    }
  }, [org.id, outcomeImportPreview, maybeStartOutcomeImportRequest]);

  const chooseAnotherFile = useCallback(() => {
    setOutcomeImportPreview(undefined);
    setOutcomeExportFilename("");
    setOutcomeImportError(undefined);
  }, []);

  const outcomeCount = outcomeImportPreview
    ? countExportedOutcomes(outcomeImportPreview.outcome_export_file)
    : 0;

  return (
    <Dialog
      dismissible={!isRequestPending}
      title={t(
        importedOutcomeCount !== undefined
          ? "outcome_transfer.success_title"
          : outcomeImportPreview
            ? "outcome_transfer.review"
            : "outcome_transfer.import",
      )}
      open
      onClose={onClose}
    >
      <div className="mb-4 flex flex-wrap justify-between gap-2 rounded-md bg-black-tint-95 p-3">
        <span>{t("outcome_transfer.destination")}</span>
        <strong className="min-w-0 break-words">{org.name}</strong>
      </div>
      {importedOutcomeCount !== undefined ? (
        <p role="status">
          {t(
            importedOutcomeCount === 0
              ? "outcome_transfer.success_zero"
              : "outcome_transfer.success",
            { count: importedOutcomeCount },
          )}
        </p>
      ) : outcomeImportPreview ? (
        <>
          <p className="mb-4 break-words">
            {t("outcome_transfer.file_source", {
              filename: outcomeExportFilename,
              source: outcomeImportPreview.outcome_export_file.source_organization_name,
            })}
          </p>
          <OutcomeTreeImportPreview outcomeExportFile={outcomeImportPreview.outcome_export_file} />
          {outcomeImportPreview.rubric_mismatch ? (
            <Banner className="mb-4" kind="info">
              <div>
                <p className="font-medium">{t("outcome_transfer.different_rubric")}</p>
                <p>{t("outcome_transfer.destination_impact")}</p>
              </div>
            </Banner>
          ) : (
            <p className="mb-4">{t("outcome_transfer.ready", { count: outcomeCount })}</p>
          )}
        </>
      ) : (
        <div className="space-y-3">
          <p>{t("outcome_transfer.upload_help")}</p>
          <FileUploadField
            accept="application/json"
            className="[&_.label]:h-auto [&_.label]:min-h-11 [&_.label]:max-w-full"
            disabled={isRequestPending}
            isLoading={isRequestPending}
            multiple={false}
            onDrop={(files) => {
              if (files.length === 1) previewOutcomeExportFile(files[0]);
            }}
          />
          {outcomeExportFilename && <p className="break-words">{outcomeExportFilename}</p>}
        </div>
      )}
      <Banner className="my-4" kind="info">
        {t("outcome_transfer.scope")}
      </Banner>
      {outcomeImportError && (
        <div className="my-4" role="alert">
          <Banner kind="error">
            <div>
              <p>{outcomeImportError.message}</p>
              {outcomeImportError.details?.map((detail) => (
                <p className="whitespace-pre-wrap break-words" key={detail}>
                  {detail}
                </p>
              ))}
            </div>
          </Banner>
        </div>
      )}
      {isRequestPending && <p role="status">{t("outcome_transfer.working")}</p>}
      <div className="mt-5 flex flex-wrap justify-end gap-2 [&_button]:h-auto [&_button]:min-h-[38px] [&_button]:max-w-full [&_button]:whitespace-normal">
        {outcomeImportPreview && importedOutcomeCount === undefined && (
          <SquareButton
            disabled={isRequestPending}
            kind="secondary"
            size="m"
            onClick={chooseAnotherFile}
          >
            {t("outcome_transfer.another_file")}
          </SquareButton>
        )}
        <SquareButton disabled={isRequestPending} kind="secondary" size="m" onClick={onClose}>
          {t(importedOutcomeCount === undefined ? "common.cancel" : "outcome_transfer.view_index")}
        </SquareButton>
        {outcomeImportPreview && importedOutcomeCount === undefined && (
          <SquareButton
            disabled={isRequestPending}
            kind="accent"
            size="m"
            onClick={importOutcomeTree}
          >
            {t(
              outcomeImportPreview.rubric_mismatch
                ? "outcome_transfer.destination_and_import"
                : outcomeCount === 0
                  ? "outcome_transfer.confirm_tree"
                  : "outcome_transfer.confirm",
              { count: outcomeCount },
            )}
          </SquareButton>
        )}
      </div>
    </Dialog>
  );
}
